import React, { useState, useEffect } from 'react';
import { Bell, X, Check, Film, Tv, Star, MessageCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = () => {
  const { token, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, token]);

  const fetchNotifications = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications?authorization=Bearer ${token}`);
      const data = await response.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAllRead = async () => {
    if (!token) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/notifications/mark-read?authorization=Bearer ${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_ids: [] })
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_movie': return <Film size={18} className="text-red-500" />;
      case 'new_show': return <Tv size={18} className="text-blue-500" />;
      case 'recommendation': return <Star size={18} className="text-yellow-500" />;
      default: return <MessageCircle size={18} className="text-gray-500" />;
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-white hover:text-red-500 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={22} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-gray-900 border border-gray-800 rounded-xl shadow-2xl z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <h3 className="text-white font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-red-500 hover:text-red-400 text-sm font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`flex gap-3 px-4 py-3 hover:bg-gray-800/50 transition-colors border-b border-gray-800/50 cursor-pointer ${
                      !notification.read ? 'bg-gray-800/30' : ''
                    }`}
                  >
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{notification.title}</p>
                      <p className="text-gray-400 text-xs line-clamp-2">{notification.message}</p>
                      <p className="text-gray-600 text-xs mt-1">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Push Notification Permission Request
export const PushNotificationPrompt = ({ onClose }) => {
  const [status, setStatus] = useState('pending'); // pending, granted, denied

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      setStatus('denied');
      return;
    }

    const permission = await Notification.requestPermission();
    setStatus(permission);

    if (permission === 'granted') {
      // Subscribe to push notifications
      // Note: In production, you'd register with a push service
      new Notification('CBXTV Notifications Enabled', {
        body: 'You will now receive updates about new content!',
        icon: '/favicon.ico'
      });
    }
  };

  return (
    <div className="fixed bottom-4 left-4 bg-gray-900 border border-gray-800 rounded-xl p-4 shadow-2xl max-w-sm z-50">
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-gray-500 hover:text-white"
      >
        <X size={18} />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="bg-red-600/20 p-2 rounded-lg">
          <Bell size={24} className="text-red-500" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Enable Notifications</h3>
          <p className="text-gray-400 text-sm mt-1">
            Get notified about new movies, TV shows, and exclusive content.
          </p>
          {status === 'pending' && (
            <button
              onClick={requestPermission}
              className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Enable Notifications
            </button>
          )}
          {status === 'granted' && (
            <p className="mt-3 text-green-400 text-sm flex items-center gap-1">
              <Check size={16} /> Notifications enabled!
            </p>
          )}
          {status === 'denied' && (
            <p className="mt-3 text-gray-500 text-sm">
              Notifications blocked. Enable in browser settings.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationBell;
